import './template-list-item.scss';

import { kebabCase } from 'lodash';
import { useMemo } from 'react';

import {
  TemplateItemDataFragment,
  TemplateStatus,
} from '../../../data/types/api/graphql';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Tag, TagType } from '../../../elements/tag/tag';

export enum TemplateListItemType {
  DEFAULT = 'DEFAULT',
  ACTIVE = 'ACTIVE',
  NOT_ACTIVE = 'NOT_ACTIVE',
}

type TemplateListItemProps = {
  type?: TemplateListItemType;
  template: TemplateItemDataFragment;
  onClick?: (value: TemplateItemDataFragment) => void;
};

export function TemplateListItem({
  template,
  onClick,
  ...props
}: TemplateListItemProps) {
  const type = useMemo(() => {
    if (props.type) {
      return props.type;
    }

    if (template.status === TemplateStatus.Active) {
      return TemplateListItemType.ACTIVE;
    }

    if (
      template.status === TemplateStatus.Deleted ||
      template.status === TemplateStatus.Unresolved
    ) {
      return TemplateListItemType.NOT_ACTIVE;
    }

    return TemplateListItemType.DEFAULT;
  }, [props.type, template.status]);

  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(template),
        onKeyDown: makeKeyDownHandler(() => onClick(template)),
      };
    }

    return {};
  }, [template, onClick]);

  return (
    <div
      {...attributes}
      className={`template-list-item template-list-item--${type}`}
    >
      <div className="template-list-item__content">
        <p>{template.name}</p>
      </div>

      <div className="template-list-item__content">
        <p>
          {template.honorific && (
            <CustomFormattedMessage
              id={`common.honorific.${kebabCase(template.honorific)}`}
            />
          )}
        </p>
      </div>
      <div className="health-professional-list-item__tags">
        {template.isApplied && (
          <Tag
            type={TagType.ORANGE}
            text={<CustomFormattedMessage id="common.used" />}
          />
        )}

        {template.status === TemplateStatus.Active && (
          <Tag type={TagType.ACTIVE} />
        )}

        {(template.status === TemplateStatus.Deleted ||
          template.status === TemplateStatus.Unresolved) && (
          <Tag type={TagType.NOTACTIVE} />
        )}
      </div>
    </div>
  );
}
